/**
 * Utility for performing word-level diffing between two strings.
 */

export interface DiffPart {
  value: string;
  added?: boolean;
  removed?: boolean;
}

/**
 * Computes a word-level diff between two strings using the Longest Common Subsequence (LCS) algorithm.
 * 
 * @param oldStr The original string.
 * @param newStr The modified string.
 * @returns An array of DiffPart objects representing the differences.
 */
export function diffWords(oldStr: string, newStr: string): DiffPart[] {
  if (oldStr === newStr) {
    return [{ value: newStr }];
  }
  
  if (!oldStr) {
    return [{ value: newStr, added: true }];
  }
  
  if (!newStr) {
    return [{ value: oldStr, removed: true }];
  }

  // Tokenize while preserving whitespace and common punctuation
  const tokenize = (s: string) => {
    // Regex matches words, or sequences of whitespace, or single punctuation marks
    return s.split(/(\s+|[.,!?;:])/).filter(t => t !== '');
  };
  
  const oldTokens = tokenize(oldStr);
  const newTokens = tokenize(newStr);
  
  const m = oldTokens.length;
  const n = newTokens.length;
  
  // DP table for LCS
  // Note: For very large strings, this O(M*N) approach might be slow, 
  // but for "facts" (typically short sentences) it's efficient.
  const dp = Array.from({ length: m + 1 }, () => new Int32Array(n + 1));
  
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldTokens[i - 1] === newTokens[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  
  // Reconstruct the diff from the DP table
  const parts: DiffPart[] = [];
  let i = m, j = n;
  
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldTokens[i - 1] === newTokens[j - 1]) {
      parts.unshift({ value: oldTokens[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      // Prioritize deletions over additions for better visualization in some cases,
      // but here we just follow the DP table.
      parts.unshift({ value: newTokens[j - 1], added: true });
      j--;
    } else {
      parts.unshift({ value: oldTokens[i - 1], removed: true });
      i--;
    }
  }
  
  // Post-process: Merge consecutive parts of the same type (added, removed, or unchanged)
  const merged: DiffPart[] = [];
  for (const part of parts) {
    const last = merged[merged.length - 1];
    if (last && !!last.added === !!part.added && !!last.removed === !!part.removed) {
      last.value += part.value;
    } else {
      merged.push({ ...part });
    }
  }
  
  return merged;
}
